import { useState } from "react";

import {
  correctSextant,
  formatDegMin,
  type Planet,
  type SunLimb,
} from "@nav-games/astro";

type BodyKind = "sun" | "moon" | "venus" | "mars" | "jupiter" | "saturn" | "star";

const BODY_LABELS: Record<BodyKind, string> = {
  sun: "Sun",
  moon: "Moon",
  venus: "Venus",
  mars: "Mars",
  jupiter: "Jupiter",
  saturn: "Saturn",
  star: "Star",
};

const BODY_ORDER: readonly BodyKind[] = [
  "sun",
  "moon",
  "venus",
  "mars",
  "jupiter",
  "saturn",
  "star",
];

function hasLimb(b: BodyKind): boolean {
  return b === "sun" || b === "moon";
}

/// Interactive Hs → Ho corrections calculator for the Reference tab.
/// Reads the sextant reading and corrections, returns Ho line-by-line
/// so the student can see the whole canonical chain.

const inputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 44,
  padding: "10px 12px",
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
};

function NumField({
  label,
  value,
  onChange,
  step = 0.1,
  min,
  max,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  step?: number;
  min?: number;
  max?: number;
  hint?: string;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 140 }}>
      <span className="small muted">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        {...(min !== undefined ? { min } : {})}
        {...(max !== undefined ? { max } : {})}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
        aria-label={label}
      />
      {hint && (
        <span className="small muted" style={{ fontSize: 11 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

function todayIso(): string {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, "0");
  const d = String(now.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function SextantCorrectionCalculator() {
  const [body, setBody] = useState<BodyKind>("sun");
  const [limb, setLimb] = useState<SunLimb>("LL");
  const [hsDeg, setHsDeg] = useState("45");
  const [hsMin, setHsMin] = useState("0");
  const [ieMin, setIeMin] = useState("2");
  const [hoe, setHoe] = useState("2");
  const [dateStr, setDateStr] = useState<string>(todayIso());

  // Compose Hs from deg + min for the navigator-friendly input form.
  const hsDecimal =
    (Number(hsDeg) || 0) + (Number(hsMin) || 0) / 60;
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
  const valid =
    hsDeg.trim() !== "" &&
    hsMin.trim() !== "" &&
    ieMin.trim() !== "" &&
    hoe.trim() !== "" &&
    Number.isFinite(hsDecimal) &&
    Number.isFinite(Number(ieMin)) &&
    Number.isFinite(Number(hoe)) &&
    Number(hoe) >= 0;

  // Date is used to derive the Sun's SD (small effect) and the Moon's
  // SD + HP (large effect). Default to "today" so Moon sights work
  // immediately; user can pick another date for back-checking sights.
  const date = validDate ? new Date(`${dateStr}T12:00:00Z`) : new Date();

  const bodyArg =
    body === "sun" || body === "moon"
      ? ({ kind: body, limb } as const)
      : body === "star"
        ? ({ kind: "star" } as const)
        : ({ kind: "planet", planet: body as Planet } as const);

  const result = valid
    ? correctSextant({
        body: bodyArg,
        hsDeg: hsDecimal,
        indexErrorMin: Number(ieMin),
        heightOfEyeM: Number(hoe),
        date,
      })
    : null;

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Sextant correction calculator</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
        Type your sextant reading and the corrections; the calculator
        walks Hs → Ho through index error, dip, refraction, semi-diameter
        and parallax in the canonical order. The Moon's parallax is big
        (~57′) and its SD swings with distance, so the date matters.
      </p>

      {/* Body. */}
      <div className="small muted" style={{ marginBottom: 4 }}>Body</div>
      <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
        {BODY_ORDER.map((b) => (
          <button
            key={b}
            type="button"
            className={body === b ? "" : "secondary"}
            onClick={() => setBody(b)}
            style={{ flex: "1 1 90px", fontSize: 14, padding: "8px 6px" }}
            aria-pressed={body === b}
          >
            {BODY_LABELS[b]}
          </button>
        ))}
      </div>

      {/* Limb — only meaningful for Sun and Moon (point sources have
          no limb to choose). */}
      {hasLimb(body) && (
        <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
          {(["LL", "UL"] as const).map((l) => (
            <button
              key={l}
              type="button"
              className={limb === l ? "" : "secondary"}
              onClick={() => setLimb(l)}
              style={{ flex: 1 }}
              aria-pressed={limb === l}
            >
              {l === "LL" ? "Lower limb" : "Upper limb"}
            </button>
          ))}
        </div>
      )}
      {!hasLimb(body) && (
        <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
          {BODY_LABELS[body]} is a point source — no semi-diameter or
          parallax correction; the chain ends after refraction.
        </p>
      )}

      {/* Date — drives Sun SD slightly and Moon SD/HP a lot. */}
      <label
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          marginBottom: 12,
        }}
      >
        <span className="small muted">Date (UTC) — sets Moon HP/SD; small effect on Sun</span>
        <input
          type="date"
          value={dateStr}
          onChange={(e) => setDateStr(e.target.value)}
          style={inputStyle}
          aria-label="Date"
        />
      </label>

      {/* Inputs. */}
      <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <NumField
          label="Hs — degrees"
          value={hsDeg}
          onChange={setHsDeg}
          step={1}
          min={0}
          max={89}
        />
        <NumField
          label="Hs — minutes"
          value={hsMin}
          onChange={setHsMin}
          step={0.1}
          min={0}
          max={59.9}
        />
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <NumField
          label="Index error (')"
          value={ieMin}
          onChange={setIeMin}
          step={0.1}
          hint="+ on the arc · − off"
        />
        <NumField
          label="Height of eye (m)"
          value={hoe}
          onChange={setHoe}
          step={0.5}
          min={0}
          max={50}
        />
      </div>

      {/* Result. */}
      {result ? (
        <>
          <div
            style={{
              textAlign: "center",
              padding: "10px 4px",
              marginBottom: 12,
              borderRadius: "var(--radius-sm)",
              border: "1px solid var(--accent)",
              background: "var(--accent-soft)",
            }}
          >
            <div className="small muted">Observed altitude (Ho)</div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>
              {formatDegMin(result.hoDeg)}
            </div>
          </div>

          <ol style={{ margin: 0, paddingLeft: 18 }}>
            {result.steps.map((s, i) => (
              <li key={i} className="small" style={{ marginBottom: 4 }}>
                {s.label}: {formatDegMin(s.fromDeg)}{" "}
                {s.deltaMin >= 0 ? "+" : "−"} {Math.abs(s.deltaMin).toFixed(1)}
                ′ = {formatDegMin(s.toDeg)}
              </li>
            ))}
          </ol>
        </>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          Enter your sextant reading and corrections to see the working.
        </p>
      )}
    </div>
  );
}
